import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button, Dropdown, Skeleton, Spin, theme } from 'antd';
import { LuDownload, LuFileDown, LuMap, LuPencil, LuTable } from 'react-icons/lu';
import { toLonLat } from 'ol/proj';

import { useVideoProject } from '@contexts/VideoProject.context';
import { useToast } from '@contexts/Toast.context';
import Loader from '@components/loading/Loader';
import { fetchUrl } from '@utils/api';
import { downloadFile } from '@utils/files';
import { getUsualSizes, rerunWithExamples, startVideoProcessing } from '@utils/api/videoProjects';
import { getUserFriendlyError } from '@utils/api/errorMessages';

import OverlayPlayer, { PlayerFrame } from './components/overlay/OverlayPlayer';
import CountPanel from './components/overlay/CountPanel';
import useCountModel from './components/overlay/useCountModel';
import FlightMap from './components/overlay/FlightMap';
import { animalPositions, indexFlight } from './components/overlay/flightModel';
import { indexLive, pointsAt } from './components/overlay/liveTracks';
import { applyCorrections, trackName } from './components/overlay/corrections';
import { keyBelongsToFocus } from './components/overlay/keyTarget';
import useCorrections from './components/overlay/useCorrections';
import { Chip } from './components/overlay/CountPanel';
import PickMenu from './components/overlay/PickMenu';
import CorrectionToolbar from './components/overlay/CorrectionToolbar';
import AssistantCard from './components/overlay/AssistantCard';
import ConfirmAdd from './components/overlay/ConfirmAdd';
import { numberProposals } from './components/overlay/AssistantAnswer';
import { smoothForDrawing } from './components/overlay/smoothTracks';
import { kindColors } from './components/overlay/kinds';
import { indexTracks } from './components/overlay/tracksModel';
import { anyOne, inViewTitle, isReindeer } from './components/overlay/subjectWords';
import VersionPicker from './flow/VersionPicker';
import { fitStage } from './components/overlay/stageLayout';
import VideoPlayer from './components/results/VideoPlayer';
import KeyMetrics from './components/results/KeyMetrics';
import DetectionChart from './components/results/DetectionChart';
import VideoFlowShell from './flow/VideoFlowShell';
import GpuStatus, { gpuStatus } from './flow/GpuStatus';
import StepRail from './flow/StepRail';
import JobProgressPanel, { jobPhase } from './flow/JobProgressPanel';
import { RateStrip, FilmStrip, FILM_STRIP_N, stripTimes } from './flow/Strips';
import { ProcessedOverlay } from './flow/RangeOverlay';
import {
	ProjectCard,
	ProjectDetails,
	methodLabel,
	jobModelLabel,
	formatDuration,
} from './flow/ProjectCards';
import {
	useSignedUrl,
	useSignedUrlState,
	useJsonFile,
	useJsonFileState,
	useLiveJsonFile,
	sourceVideoKey,
} from './flow/useResultFiles';
import {
	FlowPanel,
	PanelTitle,
	Muted,
	UNDER_PANEL_MIN_HEIGHT,
	CentreFit,
	UnderPanel,
	UnderHead,
	Label,
} from './flow/primitives';

/**
 * The video project page in the three-column flow (docs/video-flow-design/VIDEO_FLOW_DESIGN.html, screens 4-6):
 * analysing / waiting for a GPU / stopped while a job runs, and the results with the detections drawn on the video.
 * Jobs from before tracks.json existed keep the old player and metrics inside the same frame.
 */

const csvOf = rows => rows.map(r => r.join(',')).join('\n');

// the video and, when the video carries flight data, the map of the flight beside it; a stage narrower than this
// shows the map as a minimap in the corner of the video instead
const SIDE_BY_SIDE_MIN_WIDTH = 1040;

/**
 * The results stage: video (and map) on top, the timeline strip under both. The rows and the video column come
 * from fitStage once the stage is measured, so the video is as large as the fixed-height page allows and the
 * strip takes the slack; until then the video is width-driven.
 */
const Stage = styled.div`
	flex: 1;
	min-height: 0;
	display: grid;
	grid-template-columns: ${({ $side }) =>
		$side ? 'minmax(0, 1.6fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
	grid-template-rows: auto minmax(0, 1fr);
	grid-template-areas: ${({ $side }) =>
		$side ? `'video map' 'strip strip'` : `'video' 'strip'`};
	align-content: start;
	gap: ${({ theme }) => theme.token.marginSM}px;
`;

const Cell = styled.div`
	grid-area: ${({ $area }) => $area};
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
	justify-self: ${({ $centre }) => ($centre ? 'center' : 'stretch')};
	max-width: 100%;
`;

// the spinner in the middle of the player's frame while the results load
const FrameCentre = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// m:ss
const clock = sec => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

// a toggle in the player bar, in the count panel's chip style: green when on
const BarChip = ({ label, on, onChange }) => (
	<Chip type="button" role="switch" aria-checked={on} $active={on} onClick={() => onChange(!on)}>
		{label}
	</Chip>
);

const VideoProjectView = () => {
	const {
		project,
		projectUuid,
		isLoading,
		error,
		refetch,
		videoUuid,
		videoData,
		setSelectedJobUuid,
		resultsData: job,
		analysisData: analysis,
	} = useVideoProject();
	const { addToast } = useToast();
	// a correction the server refused is rolled back; say so, since the page already showed it as done
	const showSaveError = err => {
		// no response: the connection; a response: the server refused it (reported, as it is unexpected)
		if (err?.response) console.error('Correction not saved:', err);
		addToast({
			id: 'video-correction-error',
			title: 'Change not saved',
			// the server's own sentence (it sends it as `message`); a 5xx says only that it was not accepted
			message: err?.response
				? getUserFriendlyError(err, { 500: 'The server did not accept this change.' })
				: 'The change could not be saved. Check the connection and try again.',
			bg: 'danger',
		});
	};
	// a demo project is looked at, not changed (the server answers 403); an older server does not say: as before
	const readOnly = project?.can_change === false;
	const queryClient = useQueryClient();
	const playerRef = useRef(null);
	const [showDetections, setShowDetections] = useState(true);
	const [showTrails, setShowTrails] = useState(true);
	// detections as dots or boxes; the Detections chip goes dots → boxes → off; the choice is kept for next time
	const [marker, setMarker] = useState(() => {
		try {
			return localStorage.getItem('videoDetectionMarker') === 'box' ? 'box' : 'dot';
		} catch {
			return 'dot';
		}
	});
	const cycleDetections = useCallback(() => {
		const next = !showDetections ? 'dot' : marker === 'dot' ? 'box' : null;
		setShowDetections(next != null);
		if (next) {
			setMarker(next);
			try {
				localStorage.setItem('videoDetectionMarker', next);
			} catch {
				// a private window: the choice lasts this visit only
			}
		}
	}, [showDetections, marker]);
	const [showLine, setShowLine] = useState(true);
	const [showMap, setShowMap] = useState(true);
	// on a narrow stage the map is a small view in the corner of the video; this swaps the two
	const [mapLarge, setMapLarge] = useState(false);
	const { token } = theme.useToken();
	const [stageEl, setStageEl] = useState(null);
	// the stage's box and the height of the player's control bar, which the video fit has to leave room for
	const [stageBox, setStageBox] = useState({ width: 0, height: 0, chrome: 0 });
	useEffect(() => {
		if (!stageEl) return undefined;
		const controls = playerRef.current?.controls?.();
		const measure = () => {
			const chrome = controls
				? Math.round(
						controls.getBoundingClientRect().height +
							parseFloat(getComputedStyle(controls).marginTop || 0)
					)
				: 0;
			const next = { width: stageEl.clientWidth, height: stageEl.clientHeight, chrome };
			setStageBox(prev =>
				prev.width === next.width &&
				prev.height === next.height &&
				prev.chrome === next.chrome
					? prev
					: next
			);
		};
		const ro = new ResizeObserver(measure);
		ro.observe(stageEl);
		if (controls) ro.observe(controls);
		return () => ro.disconnect();
	}, [stageEl]);
	const [retrying, setRetrying] = useState(false);

	const phase = jobPhase(job);
	// while the analysis waits for a GPU or runs, the video stands still: it does not start by itself, it stops when a
	// run starts on this page (Try again, or the job loads after the video), and Play, a click on it and Space do not
	// play it; it still seeks and steps
	const analysing = phase === 'running' || phase === 'waiting';
	useEffect(() => {
		if (analysing) playerRef.current?.video?.()?.pause();
	}, [analysing]);
	// frames done so far while the animals are found and tracked (backend: video_detection._live_progress)
	// people's edits to this analysis, kept as a layer (corrections.js); they apply while it streams and after
	const {
		layer,
		add: addEdit,
		undo: undoEdit,
		price,
		lookAgain,
		hint,
		accept,
		retry: retryFollow,
		assistantBusy,
		planCheck,
		startCheck,
	} = useCorrections(projectUuid, job?.uuid, { onSaveError: showSaveError });
	const live = phase === 'running' && job?.live_progress?.frames_total ? job.live_progress : null;
	// the animals found so far (live_tracks.json, replaced every ~20 s): dots on the video and the thumbnails
	const liveDoc = useLiveJsonFile(live?.tracks || null, live?.tracks_frames);
	// the project's duration_seconds is often empty: the strip reports the video's own length
	const [stripSeconds, setStripSeconds] = useState(null);
	const videoSeconds = videoData?.duration_seconds || stripSeconds;
	// job.live_progress is a new object on every poll: the indexes follow whether the job is live and the file, not it
	const isLive = !!live;
	const liveIndex = useMemo(
		() => (isLive ? indexLive(applyCorrections(liveDoc, layer), videoSeconds) : null),
		[isLive, liveDoc, layer, videoSeconds]
	);
	const liveMarks = useMemo(
		() =>
			liveIndex && videoSeconds
				? stripTimes(FILM_STRIP_N, videoSeconds).map(t => pointsAt(liveIndex, t))
				: null,
		[liveIndex, videoSeconds]
	);
	const paths = analysis?.result_paths || {};
	const tracksFile = useJsonFileState(phase === 'finished' ? paths.tracks : null);
	const tracksDoc = tracksFile.value;
	const cleanFile = useSignedUrlState(phase === 'finished' ? paths.clean_video : null);
	const cleanUrl = cleanFile.value;
	// the result's files could not be read (a link not signed, a 403/404): said, with a retry, not loading for ever
	const resultError = tracksFile.error || cleanFile.error;
	const retryResult = () => {
		tracksFile.retry();
		cleanFile.retry();
	};
	const legacy = phase === 'finished' && !(paths.tracks && paths.clean_video);
	const maskedUrl = useSignedUrl(legacy ? paths.masked_video : null);
	const timeSeries = useJsonFile(legacy ? paths.time_series : null);
	const sourceUrl = useSignedUrl(phase !== 'finished' ? sourceVideoKey(videoData) : null);
	const telemetryDoc = useJsonFile(phase === 'finished' ? paths.telemetry : null);
	const flight = useMemo(() => indexFlight(telemetryDoc), [telemetryDoc]);

	// what is counted, as typed or suggested ("car, person"); our reindeer model's jobs count reindeer; else "object"
	const subjectText = (
		tracksDoc?.subject ||
		job?.text_prompt ||
		(job?.model_id === 'SEARCH' ? '' : 'reindeer')
	).trim();
	// the word a person asked for when one search counts several kinds ("find all vehicles" → "vehicle"): the texts
	// say it rather than the list of kinds
	const category = tracksDoc?.category || job?.category || null;
	const namedAs = category || subjectText;
	// the usual size of what is counted (from the assistant, kept per word): where the size filter starts
	const { data: usualSizes } = useQuery({
		queryKey: ['videoUsualSizes', projectUuid, job?.uuid],
		queryFn: () => getUsualSizes(projectUuid, job.uuid),
		enabled: !!projectUuid && !!job?.uuid && phase === 'finished',
		select: res => res?.data?.sizes || null,
		staleTime: Infinity,
		retry: false,
	});
	const m = useCountModel({
		tracksDoc,
		statistics: analysis?.statistics,
		virtualLine: analysis?.virtual_line,
		flight,
		layer,
		subject: subjectText,
		usualSizes,
	});
	const { setFrame } = m;
	// several kinds in one search: a colour each, on the video and in the panel
	const kindColorMap = useMemo(() => kindColors(m.kinds || []), [m.kinds]);
	// what the player draws: the same tracks smoothed over time and on every frame (smoothTracks.js); the counts keep
	// m.index / liveIndex
	const drawIndex = useMemo(
		() => (m.index ? indexTracks(smoothForDrawing(m.index.doc)) : null),
		[m.index]
	);
	const liveDrawIndex = useMemo(
		() =>
			isLive
				? indexLive(smoothForDrawing(applyCorrections(liveDoc, layer)), videoSeconds)
				: null,
		[isLive, liveDoc, layer, videoSeconds]
	);
	const onFrame = useCallback(f => setFrame(f), [setFrame]);
	const seek = useCallback(sec => playerRef.current?.seekTo(sec), []);
	const fps = m.index?.fps;
	const seekFrame = useCallback(f => fps && seek((f + 0.5) / fps), [fps, seek]);

	// correcting the result on the video: click a dot, then remove it, say it is not the subject, or merge it with
	// the dot that is the same object; Ctrl/Cmd+Z takes the last edit back
	// one of the things counted, for the correction texts: "car", "car or person"
	const subject = anyOne(namedAs);
	const [pick, setPick] = useState(null);
	const [mergeFrom, setMergeFrom] = useState(null);
	// "Correct detections": the video pauses and a tool is chosen (design: corrections_design.html, section 2) —
	// 'click' adds the object under a click, 'box' a drawn box, 'remove' picks dots; Done goes back to watching
	const [tool, setTool] = useState(null);
	const correcting = tool != null;
	// switching to another run (run again, try again, a version): its Look again / Tell it panel only exists once
	// it is finished, so leave those tools for adding
	const showRun = useCallback(
		uuid => {
			setTool(t => (t === 'again' || t === 'tell' ? 'click' : t));
			setSelectedJobUuid(uuid);
		},
		[setSelectedJobUuid]
	);
	// a short message in the toolbar's hint pill after an action
	const [flash, setFlash] = useState(null);
	const flashTimer = useRef(null);
	const showFlash = useCallback(text => {
		setFlash(text);
		clearTimeout(flashTimer.current);
		flashTimer.current = setTimeout(() => setFlash(null), 1800);
	}, []);
	useEffect(() => () => clearTimeout(flashTimer.current), []);
	// while the job runs: the height of the player's control bar, which the centre fit leaves room for
	const [runningControls, setRunningControls] = useState(0);
	const running = phase !== 'finished';
	useEffect(() => {
		const controls = running && playerRef.current?.controls?.();
		if (!controls) return undefined;
		const ro = new ResizeObserver(() =>
			setRunningControls(
				Math.round(
					controls.getBoundingClientRect().height +
						parseFloat(getComputedStyle(controls).marginTop || 0)
				)
			)
		);
		ro.observe(controls);
		return () => ro.disconnect();
		// the player (and so its control bar) mounts with the source video
	}, [running, sourceUrl]);
	const shownIndex = live ? liveIndex : m.index;
	const fpsShown = shownIndex?.fps;
	// the live view numbers frames from the source video's start; edits use the analysed clip's frames
	const clipFrame = useCallback(
		f => f - (isLive ? liveIndex?.grid?.from || 0 : 0),
		[isLive, liveIndex]
	);
	const pickedTrack = pick ? shownIndex?.tracks.find(tr => tr.id === pick.id) : null;
	const closePick = useCallback(() => {
		setPick(null);
		setMergeFrom(null);
	}, []);
	const onPick = useCallback(
		p => {
			if (mergeFrom != null && p.id !== mergeFrom) {
				addEdit({
					type: 'merge',
					tracks: [mergeFrom, p.id],
					frame: clipFrame(p.frame),
					tool: 'dot',
				});
				closePick();
				return;
			}
			setMergeFrom(null);
			if (tool === 'remove') {
				// an object someone added is taken back (its add undone), not added and removed again
				const tr = shownIndex?.tracks.find(t => t.id === p.id);
				if (tr?.added && tr.edit) {
					undoEdit(tr.edit);
					showFlash('Your added object is taken out');
					return;
				}
				addEdit({ type: 'remove', tracks: [p.id], frame: clipFrame(p.frame), tool: 'dot' });
				showFlash('Removed from every frame · Undo in the panel');
				return;
			}
			setPick(p);
		},
		[mergeFrom, addEdit, undoEdit, shownIndex, clipFrame, closePick, tool, showFlash]
	);
	const editPicked = useCallback(
		type => {
			if (!pick) return;
			addEdit({ type, tracks: [pick.id], frame: clipFrame(pick.frame), tool: 'dot' });
			closePick();
		},
		[pick, addEdit, clipFrame, closePick]
	);
	const lastEdit = layer?.edits?.length ? layer.edits[layer.edits.length - 1] : null;
	const startCorrecting = useCallback(() => {
		playerRef.current?.video?.()?.pause();
		setTool('click');
	}, []);
	// an object clicked or boxed waits here until Add is pressed (a click can land in the wrong place)
	const [confirmAdd, setConfirmAdd] = useState(null);
	const stopCorrecting = useCallback(() => {
		setTool(null);
		setConfirmAdd(null);
		closePick();
	}, [closePick]);
	const chooseTool = useCallback(
		next => {
			if (next !== 'remove') closePick();
			setConfirmAdd(null);
			setTool(next);
		},
		[closePick]
	);
	const addConfirmed = useCallback(() => {
		if (!confirmAdd) return;
		const { f, point, box, tool: how } = confirmAdd;
		const label = confirmAdd.kind || m.kinds?.[0];
		addEdit({
			type: 'add',
			frame: clipFrame(f),
			...(box ? { box } : { point }),
			tool: how,
			...(m.kinds?.length && label ? { label } : {}),
		});
		setConfirmAdd(null);
		showFlash(
			'Added · it is now followed through the video (the first time a machine starts, about 2 min)'
		);
	}, [confirmAdd, addEdit, clipFrame, showFlash, m.kinds]);
	useEffect(() => {
		const onKey = e => {
			if (keyBelongsToFocus(e)) return;
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
				if (lastEdit && !lastEdit.pending) {
					e.preventDefault();
					undoEdit(lastEdit.id);
				}
				return;
			}
			if (!correcting || e.metaKey || e.ctrlKey || e.altKey) return;
			const v = playerRef.current?.video?.();
			const key = e.key.toLowerCase();
			const tools = { a: 'click', b: 'box', r: 'remove' };
			if (tools[key]) chooseTool(tools[key]);
			// Enter adds the object waiting for its confirmation; Escape closes what is open first, then leaves
			// (not when a button has focus: Enter is that button's own press, e.g. Cancel)
			else if (e.key === 'Enter' && confirmAdd && !e.target.closest?.('button, a'))
				addConfirmed();
			else if (e.key === 'Escape') {
				if (confirmAdd) setConfirmAdd(null);
				else if (pick || mergeFrom != null) closePick();
				else stopCorrecting();
			} else if (e.key === ' ' && v) {
				if (!v.paused) v.pause();
				else if (!analysing) v.play();
			} else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && v) {
				v.pause();
				const sec =
					(e.shiftKey ? 1 : 1 / (fps || liveDoc?.fps || 30)) *
					(e.key === 'ArrowLeft' ? -1 : 1);
				v.currentTime = Math.max(0, v.currentTime + sec);
			} else return;
			e.preventDefault();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [
		lastEdit,
		undoEdit,
		correcting,
		chooseTool,
		stopCorrecting,
		fps,
		liveDoc,
		pick,
		mergeFrom,
		closePick,
		confirmAdd,
		addConfirmed,
		analysing,
	]);
	const pickMenu = (
		<PickMenu
			pick={pick}
			subject={pickedTrack?.label || subject}
			about={
				pickedTrack && fpsShown
					? `${trackName(pickedTrack.id)} · ${clock(pickedTrack.t[0] / fpsShown)}–${clock(
							pickedTrack.t[pickedTrack.t.length - 1] / fpsShown
						)}`
					: ''
			}
			added={!!pickedTrack?.added}
			merging={mergeFrom != null}
			onRemove={() => editPicked('remove')}
			onNot={() => editPicked('negative')}
			onMerge={() => pick && setMergeFrom(pick.id)}
			onUndoAdd={() => {
				if (pickedTrack?.edit) undoEdit(pickedTrack.edit);
				closePick();
			}}
			onClose={closePick}
		/>
	);
	// the assistant works on the frame shown (the analysed clip's frame numbering)
	const currentClipFrame = useCallback(() => {
		const sec = playerRef.current?.now?.() || 0;
		const doc = isLive ? liveDoc : tracksDoc;
		const off = isLive ? doc?.time_offset_sec || 0 : 0;
		return Math.max(0, Math.round((sec - off) * (doc?.fps || 30)));
	}, [isLive, liveDoc, tracksDoc]);
	// the assistant's proposals on the video, numbered as in the panel's answer
	const proposalMarks = useMemo(
		() =>
			numberProposals((layer?.edits || []).filter(e => e.status === 'proposed')).map(e =>
				e.type === 'add'
					? {
							n: e.n,
							frame: e.frame + (live ? liveIndex?.grid?.from || 0 : 0),
							box: e.box,
						}
					: { n: e.n, negative: e.tracks?.[0] }
			),
		[layer, live, liveIndex]
	);
	const [answer, setAnswer] = useState(null);
	// another version of the analysis: nothing picked or waiting for a confirmation belongs to it
	useEffect(() => {
		setPick(null);
		setMergeFrom(null);
		setConfirmAdd(null);
		setAnswer(null);
	}, [job?.uuid]);
	const correctButton = !correcting && !readOnly && (
		<Chip type="button" onClick={startCorrecting}>
			<LuPencil /> Correct detections
		</Chip>
	);
	// objects added by a click or a box that are not followed yet (or could not be), on the frames shown
	const pendingAdds = useMemo(() => {
		const following =
			layer?.follow?.status === 'starting' ? 'a machine is starting…' : 'following…';
		const out = (layer?.edits || [])
			.filter(e => e.type === 'add' && (e.status === 'following' || e.status === 'failed'))
			.map(e => ({
				frame: e.frame + (live ? liveIndex?.grid?.from || 0 : 0),
				box: e.box,
				point: e.point,
				status: e.status,
				label: e.status === 'following' ? following : null,
			}));
		if (confirmAdd)
			out.push({
				frame: confirmAdd.f,
				box: confirmAdd.box,
				point: confirmAdd.point,
				status: 'confirm',
			});
		return out;
	}, [layer, live, liveIndex, confirmAdd]);
	// the frames people corrected, as ticks on the seek bar (the video shown starts at the clip's start when finished)
	const editMarks = useMemo(() => {
		const rate = shownIndex?.fps;
		if (!rate) return null;
		const from = live ? liveIndex?.grid?.from || 0 : 0;
		return (layer?.edits || [])
			.filter(e => e.frame != null && e.status !== 'proposed' && e.status !== 'unmatched')
			.map(e => ({
				sec: (e.frame + from) / rate,
				kind: e.type === 'add' ? 'add' : 'remove',
			}));
	}, [layer, live, liveIndex, shownIndex]);
	const correctionLayer = correcting && (
		<>
			<CorrectionToolbar
				subject={subject}
				tool={tool}
				onTool={chooseTool}
				onDone={stopCorrecting}
				flash={flash}
				assistant={phase === 'finished'}
			/>
			{pickMenu}
			<ConfirmAdd
				at={confirmAdd?.at}
				subject={subject}
				kinds={m.kinds || []}
				kind={confirmAdd?.kind}
				onKind={k => setConfirmAdd(c => (c ? { ...c, kind: k } : c))}
				colors={kindColorMap}
				onAdd={addConfirmed}
				onCancel={() => setConfirmAdd(null)}
			/>
		</>
	);
	// run again with what people taught it: a new analysis version, shown as it runs; the corrections follow it
	const runAgain = useCallback(async () => {
		try {
			const res = await rerunWithExamples(projectUuid, job.uuid);
			showRun(res?.data?.job_uuid || null);
			await refetch();
			queryClient.invalidateQueries({ queryKey: ['videoResults', projectUuid] });
		} catch (err) {
			addToast({
				id: 'video-rerun-error',
				bg: 'danger',
				title: 'Could not run again',
				message: getUserFriendlyError(err),
			});
		}
	}, [projectUuid, job, showRun, refetch, queryClient, addToast]);
	const versionPicker = (
		<VersionPicker
			projectUuid={projectUuid}
			jobUuids={project?.jobs || []}
			current={job?.uuid}
			onPick={showRun}
		/>
	);
	// the assistant's question, in the count panel while Look again or Tell it what it missed is the tool
	const assistantCard = (tool === 'again' || tool === 'tell') && (
		<AssistantCard
			tool={tool}
			subject={subject}
			at={clock(playerRef.current?.now?.() || 0)}
			price={price}
			busy={assistantBusy}
			onLookAgain={() => lookAgain(currentClipFrame())}
			onHint={text => hint({ frame: currentClipFrame(), text })}
			check={layer?.check}
			onPlanCheck={planCheck}
			onStartCheck={startCheck}
			onAnswer={setAnswer}
		/>
	);
	const pickProps = {
		onPick: correcting ? onPick : null,
		selectedIds: [pick?.id, mergeFrom].filter(v => v != null),
		proposals: proposalMarks,
		pendingAdds,
		editMarks,
		tool: correcting ? tool : null,
		onAddPoint: (f, point, at) => setConfirmAdd({ f, point, tool: 'click', at }),
		onAddBox: (f, box, at) => setConfirmAdd({ f, box, tool: 'box', at }),
	};

	const settings = useMemo(
		() => (job ? [methodLabel(job.counting_method), jobModelLabel(job)].filter(Boolean) : []),
		[job]
	);
	const analysedSeconds =
		job?.analysis_end_seconds != null
			? job.analysis_end_seconds - (job.analysis_start_seconds || 0)
			: videoData?.duration_seconds;

	const exportPdf = useCallback(async () => {
		try {
			const data = await fetchUrl(`filelink?key=${encodeURIComponent(paths.report_pdf)}`);
			// fetchUrl does not throw: no link is the failure
			if (!data?.url) throw new Error(data?.error || 'no link for the report');
			downloadFile({ url: data.url, title: 'detection_report', fileExtension: 'pdf' });
		} catch (err) {
			console.error('Export failed:', err);
			addToast({
				id: 'export-pdf-error',
				bg: 'danger',
				title: 'Export failed',
				message: 'Could not download the detection report. Please try again.',
			});
		}
	}, [paths.report_pdf, addToast]);

	const animals = useMemo(() => animalPositions(m.index, flight), [m.index, flight]);

	const exportMap = useCallback(() => {
		if (!flight) return;
		const fps = m.index?.fps || flight.doc.fps || 30;
		const offset = flight.doc.time_offset_sec || 0;
		const lonLat = p => toLonLat(p).map(v => Number(v.toFixed(7)));
		const features = [
			{
				type: 'Feature',
				geometry: { type: 'LineString', coordinates: flight.pos.map(lonLat) },
				properties: { kind: 'flight_path' },
			},
			...animals.map(a => ({
				type: 'Feature',
				geometry: { type: 'Point', coordinates: lonLat(a.at) },
				properties: {
					kind: 'counted',
					track_id: m.index.tracks[a.track]?.id,
					counted_at_seconds: Number((offset + a.frame / fps).toFixed(2)),
					...(isReindeer(subjectText) ? { calf_sized: a.calf } : {}),
				},
			})),
		];
		const blob = new Blob([JSON.stringify({ type: 'FeatureCollection', features })], {
			type: 'application/geo+json',
		});
		const url = URL.createObjectURL(blob);
		downloadFile({ url, title: 'flight_map', fileExtension: 'geojson' });
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}, [flight, animals, m.index]);

	const exportCsv = useCallback(() => {
		if (!m.index || !m.series) return;
		const fps = m.index.fps;
		const offset = m.index.doc?.time_offset_sec || 0;
		const rows = [['time_seconds', 'count', 'in_view']];
		for (let f = 0; f < m.series.length; f += Math.max(1, Math.round(fps)))
			rows.push([(offset + f / fps).toFixed(2), m.series[f], m.index.inView[f]]);
		const url = URL.createObjectURL(new Blob([csvOf(rows)], { type: 'text/csv' }));
		downloadFile({ url, title: `counts_${m.mode}`, fileExtension: 'csv' });
		setTimeout(() => URL.revokeObjectURL(url), 10000);
	}, [m.index, m.series, m.mode]);

	// Re-run with the stored settings. A line job also needs its line, which the job record does not return yet.
	const canRetry =
		phase === 'stopped' && job && job.counting_method !== 'line_based' && !readOnly;
	const retry = useCallback(async () => {
		setRetrying(true);
		try {
			const configuration = Object.fromEntries(
				Object.entries({
					counting_scenario: job.counting_scenario,
					counting_method: job.counting_method,
					text_prompt: job.text_prompt,
					model_id: job.model_id,
					model_version: job.model_version,
					target_fps: job.target_fps,
					analysis_start_seconds: job.analysis_start_seconds,
					analysis_end_seconds: job.analysis_end_seconds,
				}).filter(([, v]) => v != null)
			);
			const res = await startVideoProcessing({ projectUuid, videoUuid, configuration });
			showRun(res?.data?.job_uuid || null);
			await refetch();
			queryClient.invalidateQueries({ queryKey: ['videoResults', projectUuid] });
		} catch (err) {
			addToast({
				id: 'video-retry-error',
				bg: 'danger',
				title: 'Could not start again',
				message: getUserFriendlyError(err),
			});
		} finally {
			setRetrying(false);
		}
	}, [job, projectUuid, videoUuid, showRun, refetch, queryClient, addToast]);

	const crumbs = [
		{ title: 'Projects', link: '/projects' },
		{ title: 'Video counting' },
		{ title: project?.title || 'Video project' },
	];

	if (isLoading || error || !project)
		return (
			<VideoFlowShell
				crumbs={crumbs}
				centre={
					isLoading ? (
						<Loader />
					) : (
						<FlowPanel>
							<PanelTitle>Could not load the project</PanelTitle>
							<Muted>{error?.message || 'This video project was not found.'}</Muted>
						</FlowPanel>
					)
				}
			/>
		);

	const finishedOverlay = phase === 'finished' && m.index && cleanUrl;
	const projectCard = (
		<ProjectCard project={project} video={videoData} analysis={analysis || job} />
	);

	let left;
	let centre;
	let right;
	if (phase === 'finished') {
		if (finishedOverlay) {
			const line = m.mode === 'line' && showLine ? m.line : null;
			const mapOn = !!flight && showMap;
			const sideBySide = mapOn && stageBox.width >= SIDE_BY_SIDE_MIN_WIDTH;
			const pipMap = mapOn && !sideBySide;
			const swapped = pipMap && mapLarge;
			const fit = fitStage({
				width: stageBox.width,
				height: stageBox.height,
				ratio: m.index.width / m.index.height,
				gap: token.marginSM,
				chrome: stageBox.chrome,
				side: sideBySide,
				stripFloor: UNDER_PANEL_MIN_HEIGHT,
			});
			const flightMap = mapOn && (
				<FlightMap
					flight={flight}
					index={m.index}
					frame={m.frame}
					projectUuid={projectUuid}
					onSeekFrame={seekFrame}
					animals={animals}
					compact={pipMap && !swapped}
				/>
			);
			const exportItems = [
				paths.report_pdf && {
					key: 'pdf',
					icon: <LuFileDown />,
					label: 'PDF report',
					onClick: exportPdf,
				},
				{ key: 'csv', icon: <LuTable />, label: 'CSV of counts', onClick: exportCsv },
				flight && {
					key: 'map',
					icon: <LuMap />,
					label: 'Map (GeoJSON)',
					onClick: exportMap,
				},
			].filter(Boolean);
			// no left column: the video, the map and the count get the width; the facts sit behind Details
			centre = (
				<>
					<Stage
						$side={sideBySide}
						ref={setStageEl}
						style={
							fit && {
								gridTemplateRows: `${fit.rowHeight}px ${fit.stripHeight}px`,
								gridTemplateColumns: sideBySide
									? `${fit.videoWidth}px minmax(0, 1fr)`
									: undefined,
							}
						}>
						<Cell
							$area="video"
							$centre={!sideBySide}
							style={fit ? { width: fit.videoWidth } : undefined}>
							<OverlayPlayer
								ref={playerRef}
								src={cleanUrl}
								index={drawIndex}
								marker={marker}
								kindColors={kindColorMap}
								line={line}
								lineEditable={!!line}
								onLineChange={m.setLine}
								onFrame={onFrame}
								showDetections={showDetections}
								showTrails={showTrails}
								aspect={`${m.index.width} / ${m.index.height}`}
								pip={pipMap ? flightMap : null}
								swapped={swapped}
								onSwap={() => setMapLarge(v => !v)}
								extraControls={
									<>
										<Chip
											type="button"
											$active={showDetections}
											aria-label="Detections: dots, boxes or off"
											title="Dots, boxes or off"
											onClick={cycleDetections}>
											Detections ·{' '}
											{showDetections
												? marker === 'box'
													? 'boxes'
													: 'dots'
												: 'off'}
										</Chip>
										{showDetections && (
											<BarChip
												label="Trails"
												on={showTrails}
												onChange={setShowTrails}
											/>
										)}
										{m.mode === 'line' && (
											<BarChip
												label="Line"
												on={showLine}
												onChange={setShowLine}
											/>
										)}
										{flight && (
											<BarChip
												label="Map"
												on={showMap}
												onChange={setShowMap}
											/>
										)}
										{drawIndex && correctButton}
										{versionPicker}
										<ProjectDetails
											project={project}
											video={videoData}
											analysis={analysis || job}
										/>
										<Dropdown menu={{ items: exportItems }} trigger={['click']}>
											<Chip type="button">
												<LuDownload /> Export
											</Chip>
										</Dropdown>
									</>
								}
								{...pickProps}>
								{correctionLayer}
							</OverlayPlayer>
						</Cell>
						{sideBySide && <Cell $area="map">{flightMap}</Cell>}
						<Cell $area="strip">
							<RateStrip
								series={m.index.inView}
								fps={m.index.fps}
								frame={m.frame}
								onSeek={seek}
								cumulative={false}
								title={inViewTitle(namedAs)}
							/>
						</Cell>
					</Stage>
				</>
			);
			right = (
				<CountPanel
					modes={['hover', 'line', 'max']}
					mode={m.mode}
					onModeChange={m.setMode}
					count={m.count}
					subtitle={m.subtitle}
					note={m.note}
					noteWarn={m.noteWarn}
					inView={m.index.inView[m.frame]}
					maxInView={m.index.maxInView}
					hist={m.hist}
					series={m.series}
					frame={m.frame}
					modelLabel={jobModelLabel(job)}
					sensitivity={m.sensitivity}
					subject={namedAs}
					changes={m.changes}
					edits={layer?.edits}
					assistant={assistantCard}
					sizeFilter={m.sizeFilter}
					byKind={m.byKind}
					kindColors={kindColorMap}
					category={category}
					follow={layer?.follow}
					answerKept={answer?.kept ?? null}
					answerAt={answer?.at ? `at ${answer.at}` : ''}
					onUndo={undoEdit}
					onAccept={accept}
					onRetry={retryFollow}
					fps={m.index.fps}
					onRunAgain={job?.model_id === 'SEARCH' && !readOnly ? runAgain : null}
				/>
			);
		} else if (legacy) {
			// the old player has no control bar to hold the details, so the card keeps its column
			left = projectCard;
			const stats = analysis?.statistics;
			centre = (
				<>
					<VideoPlayer
						ref={playerRef}
						videoUrl={maskedUrl}
						detections={job?.detections || []}
						virtualLine={analysis?.virtual_line}
					/>
					<DetectionChart
						data={timeSeries}
						onPointClick={p => seek(p.timestamp_sec)}
						countingMethod={stats?.counting_method_used}
					/>
				</>
			);
			right = (
				<FlowPanel $fill>
					<PanelTitle>Count</PanelTitle>
					<KeyMetrics
						subject={subjectText}
						totalCount={stats?.total_count}
						uniqueCount={stats?.unique_count}
						lineCrossNet={stats?.line_cross_net}
						average={stats?.average_count_per_frame}
						maxSimultaneousCount={stats?.max_simultaneous_count}
						countingMethod={stats?.counting_method_used}
						chartData={timeSeries}
						currentTime={0}
					/>
				</FlowPanel>
			);
		} else if (resultError) {
			centre = null;
			right = (
				<FlowPanel $fill role="alert">
					<PanelTitle>Could not load the result</PanelTitle>
					<p>
						The analysis finished and its result is saved, but it could not be read just
						now.
					</p>
					<Button type="primary" onClick={retryResult}>
						Try again
					</Button>
				</FlowPanel>
			);
		} else {
			// the results are loading: the page's own shape (player frame, strip, count panel), filled in when they come
			centre = (
				<CentreFit $ratio={16 / 9} $reserve={`0.75rem + ${UNDER_PANEL_MIN_HEIGHT}px`}>
					<div>
						<PlayerFrame>
							<FrameCentre role="status" aria-label="Loading the video">
								<Spin size="large" />
							</FrameCentre>
						</PlayerFrame>
					</div>
					<UnderPanel>
						<UnderHead>
							<Label>{inViewTitle(namedAs)}</Label>
						</UnderHead>
						<Skeleton active title={false} paragraph={{ rows: 1, width: '100%' }} />
					</UnderPanel>
				</CentreFit>
			);
			right = (
				<FlowPanel $fill aria-busy="true">
					<PanelTitle>Loading the count…</PanelTitle>
					<Skeleton active title={false} paragraph={{ rows: 8 }} />
				</FlowPanel>
			);
		}
	} else {
		const stepState =
			phase === 'waiting' ? 'waiting' : phase === 'stopped' ? 'stopped' : 'current';
		left = (
			<>
				<StepRail
					title="Steps"
					current={2}
					currentState={stepState}
					summaries={{
						0: [
							videoData?.video_filename,
							job?.analysis_end_seconds != null &&
								`${formatDuration(job.analysis_start_seconds || 0)}–${formatDuration(job.analysis_end_seconds)}`,
						]
							.filter(Boolean)
							.join(' · '),
						2:
							phase === 'waiting'
								? 'Waiting for a GPU machine'
								: phase === 'stopped'
									? 'Stopped'
									: job
										? `${Math.max(0, job.progress || 0)} %`
										: 'Not started',
					}}
				/>
				{projectCard}
			</>
		);
		const liveRatio = liveIndex ? liveIndex.width / liveIndex.height : NaN;
		const runningShape = Number.isFinite(liveRatio) && liveRatio > 0 ? liveIndex : null;
		centre = (
			<CentreFit
				$ratio={runningShape ? liveRatio : 16 / 9}
				$reserve={`${runningControls}px + 0.75rem + ${UNDER_PANEL_MIN_HEIGHT}px`}>
				<OverlayPlayer
					ref={playerRef}
					src={sourceUrl}
					index={liveDrawIndex}
					marker={marker}
					aspect={
						runningShape ? `${runningShape.width} / ${runningShape.height}` : undefined
					}
					autoPlay={!analysing}
					canPlay={!analysing}
					// correcting needs what was found so far: not while waiting for a machine or when stopped
					extraControls={liveDrawIndex ? correctButton : null}
					{...pickProps}>
					{correctionLayer}
				</OverlayPlayer>
				<FilmStrip
					src={sourceUrl}
					marks={liveMarks}
					frameSize={liveIndex}
					onDuration={setStripSeconds}
					title={live ? 'Analysed so far' : 'Preview'}
					right={
						live
							? `${live.frames_done} of ${live.frames_total} frames · ${live.counted_so_far} counted so far`
							: videoSeconds
								? `0:00 – ${formatDuration(videoSeconds)}`
								: null
					}>
					{live && (
						<ProcessedOverlay
							duration={videoSeconds}
							start={job?.analysis_start_seconds || 0}
							end={job?.analysis_end_seconds}
							frac={live.frames_total ? live.frames_done / live.frames_total : 0}
						/>
					)}
				</FilmStrip>
			</CentreFit>
		);
		right = job ? (
			<JobProgressPanel
				job={job}
				settings={settings}
				analysedSeconds={analysedSeconds}
				frameMegapixels={liveIndex ? (liveIndex.width * liveIndex.height) / 1e6 : null}
				onRetry={canRetry ? retry : null}
				retrying={retrying}
			/>
		) : (
			<FlowPanel $fill>
				<PanelTitle>No analysis yet</PanelTitle>
				<Muted>This video has not been analysed.</Muted>
			</FlowPanel>
		);
	}

	// a GPU machine starting or working (the analysis, a run again, following added objects), always in one place
	const gpu = gpuStatus({
		job,
		phase,
		follow: layer?.follow,
		following: (layer?.edits || []).filter(e => e.type === 'add' && e.status === 'following')
			.length,
	});
	return (
		<VideoFlowShell
			crumbs={crumbs}
			left={left}
			centre={centre}
			right={right}
			status={<GpuStatus status={gpu} />}
		/>
	);
};

export default VideoProjectView;
